// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";

export interface ListLineContainerProps {
    extendedStyles?: ListLineContainerStyles;
    isCondensed?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Checbox: FC<PropsWithChildren>;
    Image: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Notes: FC<PropsWithChildren>;
    NotesField: FC<PropsWithChildren>;
    DeleteItem: FC<PropsWithChildren>;
}
export interface ListLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    checbox?: InjectableCss;
    notes?: InjectableCss;
    notesField?: InjectableCss;
    deleteItem?: InjectableCss;
}

export const listLineContainerStyles: ListLineContainerStyles = {};

const names = {
    Checbox: "Checbox",
    Image: "Image",
    Description: "Description",
    Price: "Price",
    DeleteItem: "DeleteItem",
    Notes: "Notes",
    NotesField: "NotesField",
};

const Checbox: FC = () => null;
const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const DeleteItem: FC = () => null;
const Notes: FC = () => null;
const NotesField: FC = () => null;

const ListLineContainer: React.FC<PropsWithChildren & ListLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    isCondensed,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(listLineContainerStyles, extendedStyles));

    const { Image, Description, Price, DeleteItem, Checbox, Notes, NotesField } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
        const MappedChilds = childrenArray.reduce(
            (results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren>>,
        );
        const slots = Object.entries(names).reduce(
            (results, [key, value], idx) => {
                results[value] = MappedChilds[value] || null;
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container} {...otherProps} {...{ isCondensed }}>
            <StyledWrapper {...styles.checbox}>{Checbox?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.image} {...{ isCondensed }}>
                {Image?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.description} {...{ isCondensed }}>
                {Description?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.price} {...{ isCondensed }}>
                {Price?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.deleteItem} {...{ isCondensed }}>
                {DeleteItem?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.notes} {...{ isCondensed }}>
                {Notes?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.notesField} {...{ isCondensed }}>
                {NotesField?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

ListLineContainer.Checbox = Checbox;
ListLineContainer.Image = Image;
ListLineContainer.Description = Description;
ListLineContainer.Price = Price;
ListLineContainer.DeleteItem = DeleteItem;
ListLineContainer.Notes = Notes;
ListLineContainer.NotesField = NotesField;

ListLineContainer.Checbox.displayName = names.Checbox;
ListLineContainer.Image.displayName = names.Image;
ListLineContainer.Description.displayName = names.Description;
ListLineContainer.Price.displayName = names.Price;
ListLineContainer.DeleteItem.displayName = names.DeleteItem;
ListLineContainer.Notes.displayName = names.Notes;
ListLineContainer.NotesField.displayName = names.NotesField;

export default ListLineContainer;
